import { Issue } from '@/types';

const ISSUES_STORAGE_KEY = 'street_issues';

/* =====================================================
   🔥 NEW: SMART RISK & IMPACT HELPERS (ADD ONLY)
   ===================================================== */

const CATEGORY_BASE_RISK: Record<Issue['category'], number> = {
  pothole: 60,
  street_light: 40,
  garbage: 30,
  traffic_sign: 35,
  other: 20,
};

const PRIORITY_MULTIPLIER: Record<Issue['priority'], number> = {
  low: 0.8,
  medium: 1,
  high: 1.3,
};

const calculateRiskScore = (
  category: Issue['category'],
  priority: Issue['priority']
): number => {
  const base = CATEGORY_BASE_RISK[category] ?? 20;
  const multiplier = PRIORITY_MULTIPLIER[priority] ?? 1;
  return Math.min(100, Math.round(base * multiplier));
};

const generateSustainabilityImpact = (
  category: Issue['category']
): Issue['sustainabilityImpact'] => {
  switch (category) {
    case 'garbage':
      return { label: 'Waste Managed', value: '2kg' };
    case 'street_light':
      return { label: 'Energy Saved', value: '1.5 kWh/day' };
    case 'pothole':
      return { label: 'Fuel Emissions Reduced', value: '0.8kg CO₂' };
    case 'traffic_sign':
      return { label: 'Accident Risk Reduced', value: 'High' };
    default:
      return { label: 'Urban Improvement', value: 'Moderate' };
  }
};

/* =====================================================
   MOCK DATA (UNCHANGED)
   ===================================================== */

const mockIssues: Issue[] = [
  {
    id: '1',
    title: 'Street Light Not Working Near Bus Stop',
    description: 'The street light near the bus stop has not been functioning for more than a week, causing safety concerns at night.',
    category: 'street_light',
    status: 'pending',
    location: { lat: 13.0878, lng: 80.2785, address: 'Madhavaram Bus Stand Road, Chennai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400',
    reportedBy: 'Suresh Babu',
    reportedAt: new Date('2025-11-05'),
    updatedAt: new Date('2025-11-05'),
    priority: 'high'
  },
  {
    id: '2',
    title: 'Large Pothole on Main Road',
    description: 'A deep pothole is causing traffic congestion and damage to vehicles during peak hours.',
    category: 'pothole',
    status: 'in_progress',
    location: { lat: 11.0231, lng: 76.9730, address: 'Avinashi Road, Coimbatore, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=400',
    reportedBy: 'Arun Prakash',
    reportedAt: new Date('2025-11-12'),
    updatedAt: new Date('2025-11-20'),
    priority: 'high'
  },
  {
    id: '3',
    title: 'Garbage Overflow Near Residential Area',
    description: 'Garbage has not been cleared for several days, creating unhygienic conditions.',
    category: 'garbage',
    status: 'pending',
    location: { lat: 9.9252, lng: 78.1198, address: 'Anna Nagar, Madurai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1604187351574-c75ca79f5807?w=400',
    reportedBy: 'Meenakshi',
    reportedAt: new Date('2025-11-18'),
    updatedAt: new Date('2025-11-18'),
    priority: 'medium'
  },
  {
    id: '4',
    title: 'Open Drainage Overflow',
    description: 'Drainage water is overflowing onto the road causing foul smell and health risks.',
    category: 'other',
    status: 'pending',
    location: { lat: 10.8150, lng: 78.6856, address: 'Thillai Nagar, Tiruchirappalli, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400',
    reportedBy: 'Vijay Anand',
    reportedAt: new Date('2025-11-25'),
    updatedAt: new Date('2025-11-25'),
    priority: 'high'
  },
  {
    id: '5',
    title: 'Damaged Footpath Tiles',
    description: 'Broken and uneven footpath tiles are causing difficulty for pedestrians.',
    category: 'other',
    status: 'in_progress',
    location: { lat: 13.0500, lng: 80.2120, address: 'T. Nagar, Chennai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1523413651479-597eb2da0ad6?w=400',
    reportedBy: 'Priya Sharma',
    reportedAt: new Date('2025-12-01'),
    updatedAt: new Date('2025-12-06'),
    priority: 'medium'
  },
  {
    id: '6',
    title: 'Water Leakage from Pipeline',
    description: 'Continuous water leakage is wasting water and damaging the road surface.',
    category: 'other',
    status: 'pending',
    location: { lat: 8.7139, lng: 77.7567, address: 'Palayamkottai Road, Tirunelveli, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=400',
    reportedBy: 'Muthukumar',
    reportedAt: new Date('2025-12-05'),
    updatedAt: new Date('2025-12-05'),
    priority: 'high'
  },
  {
    id: '7',
    title: 'Illegal Garbage Dumping',
    description: 'Construction debris is being dumped illegally near the playground.',
    category: 'other',
    status: 'resolved',
    location: { lat: 12.9716, lng: 79.1590, address: 'Katpadi Road, Vellore, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=400',
    reportedBy: 'Karthik',
    reportedAt: new Date('2025-12-08'),
    updatedAt: new Date('2025-12-15'),
    priority: 'low'
  },
  {
    id: '8',
    title: 'Stray Dogs Causing Safety Issues',
    description: 'A group of stray dogs is creating fear among residents during early morning hours.',
    category: 'other',
    status: 'pending',
    location: { lat: 11.6643, lng: 78.1460, address: 'Fairlands, Salem, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=400',
    reportedBy: 'Anitha',
    reportedAt: new Date('2025-12-12'),
    updatedAt: new Date('2025-12-12'),
    priority: 'medium'
  },
  {
    id: '9',
    title: 'Traffic Signal Not Working',
    description: 'The traffic signal has stopped functioning, causing confusion and traffic jams.',
    category: 'traffic_sign',
    status: 'in_progress',
    location: { lat: 13.0604, lng: 80.2496, address: 'Guindy Signal, Chennai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1504215680853-026ed2a45def?w=400',
    reportedBy: 'Raghavan',
    reportedAt: new Date('2025-12-15'),
    updatedAt: new Date('2025-12-18'),
    priority: 'high'
  },
  {
    id: '10',
    title: 'Road Flooding After Rain',
    description: 'Water stagnation occurs after every rainfall making the road unusable.',
    category: 'other',
    status: 'pending',
    location: { lat: 10.9601, lng: 78.0766, address: 'Dindigul Main Road, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1605128690178-8f2c9cc38c63?w=400',
    reportedBy: 'Senthil',
    reportedAt: new Date('2025-12-20'),
    updatedAt: new Date('2025-12-20'),
    priority: 'high'
  },

  /* -------- JAN 2026 -------- */

  {
    id: '11',
    title: 'Broken Manhole Cover',
    description: 'An open manhole is posing danger to pedestrians and vehicles.',
    category: 'other',
    status: 'pending',
    location: { lat: 13.1210, lng: 80.2154, address: 'Ambattur, Chennai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1580910051074-7cc9caa4e2b7?w=400',
    reportedBy: 'Ganesh',
    reportedAt: new Date('2026-01-02'),
    updatedAt: new Date('2026-01-02'),
    priority: 'high'
  },
  {
    id: '12',
    title: 'Damaged Road Divider',
    description: 'The road divider is broken, increasing the risk of accidents.',
    category: 'other',
    status: 'in_progress',
    location: { lat: 11.3410, lng: 77.7172, address: 'Perundurai Road, Erode, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400',
    reportedBy: 'Saravanan',
    reportedAt: new Date('2026-01-04'),
    updatedAt: new Date('2026-01-10'),
    priority: 'medium'
  },
  {
    id: '13',
    title: 'Non-Functioning Public Toilet',
    description: 'The public toilet is closed and not maintained properly.',
    category: 'other',
    status: 'pending',
    location: { lat: 12.2253, lng: 79.0747, address: 'Vandavasi Road, Tiruvannamalai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400',
    reportedBy: 'Ravi',
    reportedAt: new Date('2026-01-06'),
    updatedAt: new Date('2026-01-06'),
    priority: 'medium'
  },
  {
    id: '14',
    title: 'Damaged Speed Breaker',
    description: 'The speed breaker is broken and not visible at night.',
    category: 'other',
    status: 'resolved',
    location: { lat: 10.0805, lng: 77.0595, address: 'Cumbum Road, Theni, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=400',
    reportedBy: 'Murugan',
    reportedAt: new Date('2026-01-08'),
    updatedAt: new Date('2026-01-15'),
    priority: 'low'
  },
  {
    id: '15',
    title: 'Overgrown Trees Blocking Street Light',
    description: 'Tree branches are blocking the street light, reducing visibility at night.',
    category: 'other',
    status: 'pending',
    location: { lat: 11.7447, lng: 79.7680, address: 'Nellikuppam, Cuddalore, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1501004318641-b39e6451bec6?w=400',
    reportedBy: 'Ramesh',
    reportedAt: new Date('2026-01-10'),
    updatedAt: new Date('2026-01-10'),
    priority: 'medium'
  },
  {
    id: '16',
    title: 'Bus Stop Shelter Damaged',
    description: 'The bus stop shelter roof is damaged and unsafe during rains.',
    category: 'other',
    status: 'in_progress',
    location: { lat: 12.9165, lng: 79.1325, address: 'Arcot Road, Ranipet, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400',
    reportedBy: 'Deepa',
    reportedAt: new Date('2026-01-12'),
    updatedAt: new Date('2026-01-18'),
    priority: 'medium'
  },
  {
    id: '17',
    title: 'Sewage Water on Road',
    description: 'Sewage water is flowing onto the road causing inconvenience and bad odor.',
    category: 'other',
    status: 'pending',
    location: { lat: 8.7285, lng: 77.7036, address: 'Vannarpettai, Tirunelveli, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400',
    reportedBy: 'Abdul Rahman',
    reportedAt: new Date('2026-01-15'),
    updatedAt: new Date('2026-01-15'),
    priority: 'high'
  },
  {
    id: '18',
    title: 'Faded Zebra Crossing',
    description: 'The zebra crossing markings have faded and are not clearly visible.',
    category: 'other',
    status: 'resolved',
    location: { lat: 13.0340, lng: 80.2619, address: 'Adyar Signal, Chennai, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400',
    reportedBy: 'Kavitha',
    reportedAt: new Date('2026-01-18'),
    updatedAt: new Date('2026-01-24'),
    priority: 'low'
  },
  {
    id: '19',
    title: 'Uncollected Construction Waste',
    description: 'Construction waste has been lying on the roadside for weeks.',
    category: 'other',
    status: 'pending',
    location: { lat: 12.8406, lng: 79.7002, address: 'Sriperumbudur, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=400',
    reportedBy: 'Sathish',
    reportedAt: new Date('2026-01-22'),
    updatedAt: new Date('2026-01-22'),
    priority: 'medium'
  },
  {
    id: '20',
    title: 'Damaged Electric Pole',
    description: 'An electric pole is tilted and appears unsafe for nearby houses.',
    category: 'other',
    status: 'in_progress',
    location: { lat: 10.3673, lng: 77.9803, address: 'Oddanchatram, Dindigul, Tamil Nadu' },
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400',
    reportedBy: 'Balaji',
    reportedAt: new Date('2026-01-25'),
    updatedAt: new Date('2026-01-28'),
    priority: 'high'
  }
];




/* =====================================================
   STORAGE SERVICE (SAFE EXTENSIONS)
   ===================================================== */

export const storageService = {
  getIssues: (): Issue[] => {
    const stored = localStorage.getItem(ISSUES_STORAGE_KEY);

    const issues: Issue[] = stored
      ? JSON.parse(stored).map((issue: Issue) => ({
          ...issue,
          reportedAt: new Date(issue.reportedAt),
          updatedAt: new Date(issue.updatedAt),
        }))
      : mockIssues;

    /* 🔥 NEW: AUTO-ENRICH EXISTING ISSUES */
    const enriched = issues.map((issue) => ({
      ...issue,
      riskScore:
        issue.riskScore ??
        calculateRiskScore(issue.category, issue.priority),
      sustainabilityImpact:
        issue.sustainabilityImpact ??
        generateSustainabilityImpact(issue.category),
    }));

    localStorage.setItem(ISSUES_STORAGE_KEY, JSON.stringify(enriched));
    return enriched;
  },

  saveIssue: (
    issue: Omit<Issue, 'id' | 'reportedAt' | 'updatedAt'>
  ): Issue => {
    const issues = storageService.getIssues();

    const newIssue: Issue = {
      ...issue,
      id: Date.now().toString(),
      reportedAt: new Date(),
      updatedAt: new Date(),
      riskScore: calculateRiskScore(issue.category, issue.priority),
      sustainabilityImpact: generateSustainabilityImpact(issue.category),
    };

    issues.push(newIssue);
    localStorage.setItem(ISSUES_STORAGE_KEY, JSON.stringify(issues));
    return newIssue;
  },

  updateIssue: (id: string, updates: Partial<Issue>): Issue | null => {
    const issues = storageService.getIssues();
    const index = issues.findIndex((issue) => issue.id === id);
    if (index === -1) return null;

    const updatedIssue: Issue = {
      ...issues[index],
      ...updates,
      updatedAt: new Date(),
    };

    /* 🔥 AUTO-RECALCULATE RISK IF NEEDED */
    if (updates.category || updates.priority) {
      updatedIssue.riskScore = calculateRiskScore(
        updatedIssue.category,
        updatedIssue.priority
      );
    }

    /* 🔥 ADD SUSTAINABILITY ON RESOLUTION */
    if (updates.status === 'resolved') {
      updatedIssue.sustainabilityImpact =
        updatedIssue.sustainabilityImpact ??
        generateSustainabilityImpact(updatedIssue.category);
    }

    issues[index] = updatedIssue;
    localStorage.setItem(ISSUES_STORAGE_KEY, JSON.stringify(issues));
    return updatedIssue;
  },

  deleteIssue: (id: string): boolean => {
    const issues = storageService.getIssues();
    const filtered = issues.filter((issue) => issue.id !== id);
    if (filtered.length === issues.length) return false;

    localStorage.setItem(ISSUES_STORAGE_KEY, JSON.stringify(filtered));
    return true;
  },
};
/* =========================
   SMART STORAGE EXTENSIONS
   (ADD-ONLY, SAFE)
   ========================= */

/** ⭐ Add citizen feedback after resolution */
export const addIssueFeedback = (
  id: string,
  rating: number,
  feedback?: string
): Issue | null => {
  return storageService.updateIssue(id, {
    rating,
    feedback,
  });
};

/** ♻ Attach sustainability impact (admin / demo use) */
export const addSustainabilityImpact = (
  id: string,
  impact: { label: string; value: string }
): Issue | null => {
  return storageService.updateIssue(id, {
    sustainabilityImpact: impact,
  });
};

/** 🧠 Ensure every issue always has a riskScore */
export const normalizeIssues = (): Issue[] => {
  const issues = storageService.getIssues();
  const updated = issues.map(issue => ({
    ...issue,
    riskScore: issue.riskScore ?? 0,
  }));
  localStorage.setItem('street_issues', JSON.stringify(updated));
  return updated;
};

/** 📊 Admin analytics helpers */
export const analyticsService = {
  getAverageRisk(): number {
    const issues = storageService.getIssues();
    if (!issues.length) return 0;
    const total = issues.reduce((sum, i) => sum + (i.riskScore ?? 0), 0);
    return Math.round(total / issues.length);
  },

  getResolvedRate(): number {
    const issues = storageService.getIssues();
    if (!issues.length) return 0;
    const resolved = issues.filter(i => i.status === 'resolved').length;
    return Math.round((resolved / issues.length) * 100);
  },

  getAverageRating(): number {
    const ratings = storageService
      .getIssues()
      .filter(i => i.rating !== undefined)
      .map(i => i.rating as number);

    if (!ratings.length) return 0;
    return Number(
      (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
    );
  }
};
